import React, { useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FixedTopBarContainer,
  UploadButton,
  FilterButtonsWrapper,
  FilterButton
} from "./FixedTopBar.styled";
import { ActionsGlobalMediaLibrary } from "../../../../Actions/ActionsGlobalMediaLibrary/ActionsGlobalMediaLibrary";
import { prepareCompanyImageFolderName } from "../../../../utils/Helpers";
import { useUploadInput } from "../../../../hooks/useUploadInput";
import { withRefluxStore } from "../../../../Stores";
import { Store__System } from "@talentbait/shared-components";
import GlobalMediaLibraryStore from "../../GlobalMediaLibraryStore";
import logger from "../../../../utils/LoggerFrontend";

const FixedTopBar = ({ organization, companyId, filters }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const { folderName } = useMemo(() => {
    try {
      const result = prepareCompanyImageFolderName(organization?.id, companyId);
      logger.log("FixedTopBar:folder:prepared", "Folder prepared", {
        folderName: result.folderName,
        companyId
      });
      return result;
    } catch (error) {
      logger.log("error:FixedTopBar:folder:failed", "Folder preparation failed", {
        error: error?.message || error,
        companyId
      });
      return { folderName: "" };
    }
  }, [organization?.id, companyId]);

  const cloudinaryContext = useMemo(() => {
    const context = `orgaId=${organization?.id}|source=template-media-library|companyId=${companyId}`;
    logger.log("FixedTopBar:context:created", "Cloudinary context created", {
      context,
      companyId
    });
    return context;
  }, [organization?.id, companyId]);

  const emitNewImageUpload = (newAsset) => {
    if (newAsset) {
      const isLogoUpload = filters?.contentType === "logos";
      const action = isLogoUpload ? "newlyUploadedLogos" : "newlyUploadedImages";

      logger.log("FixedTopBar:upload:asset", "Asset uploaded", {
        isLogoUpload,
        action,
        companyId
      });

      if (isLogoUpload) {
        ActionsGlobalMediaLibrary.newlyUploadedLogos([newAsset]);
      } else {
        ActionsGlobalMediaLibrary.newlyUploadedImages([newAsset]);
      }
    }
  };

  const { handleInputChange } = useUploadInput({
    cloudinaryContext,
    folderName,
    emitNewImageUpload,
    isLogo: filters?.contentType === "logos"
  });

  const handleUploadClick = () => {
    logger.log("FixedTopBar:upload:clicked", "Upload clicked", {
      contentType: filters?.contentType,
      companyId
    });
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      logger.log("FixedTopBar:files:selected", "Files selected", {
        fileCount: files.length,
        contentType: filters?.contentType,
        companyId
      });

      ActionsGlobalMediaLibrary.startUpload(files.length);
      handleInputChange(event);
    }
    event.target.value = "";
  };

  const handleFilterClick = (filterType) => {
    logger.log("FixedTopBar:filter:clicked", "Filter clicked", {
      filterType,
      companyId
    });
    ActionsGlobalMediaLibrary.setFilter({ filterType: "contentType", value: filterType });
  };

  return (
    <FixedTopBarContainer>
      <FilterButtonsWrapper>
        <FilterButton
          className={filters?.contentType === "all" ? "active" : ""}
          onClick={() => handleFilterClick("all")}
        >
          {t("All")}
        </FilterButton>
        <FilterButton
          className={filters?.contentType === "yourUploads" ? "active" : ""}
          onClick={() => handleFilterClick("yourUploads")}
        >
          {t("Your uploads")}
        </FilterButton>
        <FilterButton
          className={filters?.contentType === "templates" ? "active" : ""}
          onClick={() => handleFilterClick("templates")}
        >
          {t("Templates")}
        </FilterButton>
        <FilterButton
          className={filters?.contentType === "logos" ? "active" : ""}
          onClick={() => handleFilterClick("logos")}
        >
          {t("Logos")}
        </FilterButton>
      </FilterButtonsWrapper>
      <UploadButton onClick={handleUploadClick}>
        <span>{t("Upload")}</span>
      </UploadButton>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
    </FixedTopBarContainer>
  );
};

export default withRefluxStore(
  FixedTopBar,
  [Store__System, GlobalMediaLibraryStore],
  ["organization", "filters"]
);
